/**
 * Pure helpers for the Templates page: card sketches, editor paths and the "Create with Inky"
 * draft (AI outline -> template HTML in the field storage form of CONTRACTS §5).
 */
import {
  TEMPLATE_FIELD_KINDS,
  TEMPLATE_FIELD_SOURCES,
  buildFieldHtml,
  escapeHtml,
  legacyFieldId,
  wrapFieldInstruction,
} from '../../utils/templateFields';
import { templateEditorPath } from '../../utils/navigation';
import { isPresentationTemplateType } from '../documents/documentMeta';

const SKETCH_BLOCK_RE = /<(h[1-6]|p|li|div|span)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
const MAX_SKETCH_ROWS = 6;

/**
 * Editor route for a template (document or deck).
 * @param {{id: string, templateType?: string}} template - Template.
 * @returns {string} Path.
 */
export function editorPathFor(template) {
  return templateEditorPath({
    templateId: template.id,
    kind: isPresentationTemplateType(template.templateType) ? 'powerpoint-rich' : 'document',
  });
}

/**
 * Abstract "page sketch" rows for a template card thumbnail, derived from the template's
 * structure: title, headings, AI fields and body text, in order.
 * @param {string} html - Template HTML.
 * @returns {Array<{kind: 'title'|'heading'|'field'|'text', width: number}>} Rows (width in %).
 */
export function buildTemplateSketch(html) {
  const rows = [];
  const source = String(html || '');
  SKETCH_BLOCK_RE.lastIndex = 0;
  let match = SKETCH_BLOCK_RE.exec(source);
  while (match && rows.length < MAX_SKETCH_ROWS) {
    const tag = match[1].toLowerCase();
    const attrs = match[2];
    const text = match[3].replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (/data-tpl-field=/i.test(attrs) || /^\{[^{}]+\}$/.test(text)) {
      rows.push({ kind: 'field', width: 100 });
    } else if (tag === 'h1') {
      rows.push({ kind: 'title', width: Math.min(78, 36 + text.length * 1.2) });
    } else if (/^h[2-6]$/.test(tag)) {
      rows.push({ kind: 'heading', width: Math.min(58, 22 + text.length * 1.1) });
    } else if (text && tag !== 'span' && tag !== 'div') {
      rows.push({ kind: 'text', width: Math.min(96, 46 + (text.length % 50)) });
    }
    match = SKETCH_BLOCK_RE.exec(source);
  }
  if (rows.length === 0) {
    return [
      { kind: 'title', width: 60 },
      { kind: 'text', width: 90 },
      { kind: 'text', width: 72 },
    ];
  }
  return rows;
}

/** JSON schema the AI fills for "Create with Inky". */
export const INKY_TEMPLATE_SCHEMA = {
  type: 'object',
  properties: {
    name: { type: 'string', description: 'Short template name, e.g. "Statement of Work".' },
    description: { type: 'string', description: 'One sentence saying what documents this template produces.' },
    title: { type: 'string', description: 'Document title (h1) as fixed text.' },
    sections: {
      type: 'array',
      description: 'Sections in document order (3 to 12).',
      items: {
        type: 'object',
        properties: {
          heading: { type: 'string', description: 'Section heading, fixed text.' },
          fixedText: { type: 'string', description: 'Optional fixed boilerplate paragraph copied verbatim (empty when none).' },
          instruction: { type: 'string', description: 'Specific instruction for the AI that fills this section, starting with an action verb.' },
          kind: { type: 'string', enum: TEMPLATE_FIELD_KINDS, description: 'Output shape.' },
          source: { type: 'string', enum: TEMPLATE_FIELD_SOURCES, description: 'Evidence to use.' },
          required: { type: 'boolean' },
          maxWords: { type: 'integer', description: 'Word limit (40 to 600).' },
        },
        required: ['heading', 'instruction', 'kind', 'source', 'required', 'maxWords'],
        propertyOrdering: ['heading', 'fixedText', 'instruction', 'kind', 'source', 'required', 'maxWords'],
      },
    },
  },
  required: ['name', 'description', 'title', 'sections'],
  propertyOrdering: ['name', 'description', 'title', 'sections'],
};

/** System instruction for "Create with Inky". */
export const INKY_TEMPLATE_SYSTEM = `You design reusable business document templates for a project management workspace.
A template has fixed text (title, headings, optional boilerplate) and AI fields that are later filled from a project's requirements, meeting sessions and project info.
Return JSON matching the schema. Rules:
- 3 to 12 sections in a sensible order for the requested document type.
- Every section has exactly one AI field. Its instruction is specific: what to cover, how much detail, which evidence to use. Start with an action verb.
- Choose kind: "rich" for paragraphs, "list" for bullet lists, "table_rows" for tabular data such as timelines or risks, "text" for one short line.
- Choose source: "requirements", "sessions", "project" or "any".
- Only put boilerplate in fixedText when it is genuinely constant (legal notes, standard wording); otherwise leave it empty.
- Never use curly braces. Plain text only, no markdown, no HTML.`;

/**
 * Clamp an integer.
 * @param {unknown} value - Value.
 * @param {number} min - Minimum.
 * @param {number} max - Maximum.
 * @param {number} fallback - Value when not a number.
 * @returns {number} Clamped integer.
 */
const clampInt = (value, min, max, fallback) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

/**
 * Plain single-line text from model output (no braces, collapsed whitespace).
 * @param {unknown} value - Value.
 * @param {number} max - Max characters.
 * @returns {string} Text.
 */
const cleanText = (value, max) => String(value == null ? '' : value)
  .replace(/[{}]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, max);

/**
 * Turn the AI outline into template HTML using the field storage form (instruction wrapped in
 * braces inside the field element) so both the current and the previous generation pipelines
 * fill it. Field ids are deterministic (ordinal + instruction).
 * @param {object} outline - Model JSON (see INKY_TEMPLATE_SCHEMA).
 * @returns {{name: string, description: string, content: string, fieldCount: number}} Template data.
 * @throws {Error} When the outline has no usable sections.
 */
export function buildTemplateFromOutline(outline) {
  const sections = (Array.isArray(outline?.sections) ? outline.sections : [])
    .map((section) => ({
      heading: cleanText(section?.heading, 160),
      fixedText: cleanText(section?.fixedText, 1200),
      instruction: cleanText(section?.instruction, 600),
      kind: TEMPLATE_FIELD_KINDS.includes(section?.kind) ? section.kind : 'rich',
      source: TEMPLATE_FIELD_SOURCES.includes(section?.source) ? section.source : 'any',
      required: section?.required !== false,
      maxWords: clampInt(section?.maxWords, 20, 1000, 200),
    }))
    .filter((section) => section.heading && section.instruction)
    .slice(0, 20);
  if (sections.length === 0) throw new Error('Inky could not draft any sections. Try describing the document in more detail.');

  const name = cleanText(outline?.name, 120) || cleanText(outline?.title, 120) || 'New template';
  const title = cleanText(outline?.title, 160) || name;
  const parts = [`<h1>${escapeHtml(title)}</h1>`];
  sections.forEach((section, index) => {
    parts.push(`<h2>${escapeHtml(section.heading)}</h2>`);
    if (section.fixedText) parts.push(`<p>${escapeHtml(section.fixedText)}</p>`);
    const field = {
      id: legacyFieldId(index, section.instruction),
      isBlock: true,
      kind: section.kind,
      required: section.required,
      source: section.source,
      maxWords: section.maxWords,
    };
    parts.push(buildFieldHtml(field, escapeHtml(wrapFieldInstruction(section.instruction, section.required))));
  });

  return {
    name,
    description: cleanText(outline?.description, 400),
    content: parts.join('\n'),
    fieldCount: sections.length,
  };
}

/**
 * Prompt for "Create with Inky".
 * @param {{purpose: string, sections?: string, audience?: string}} input - User input.
 * @returns {string} Prompt.
 */
export function buildInkyTemplatePrompt({ purpose, sections = '', audience = '' }) {
  return [
    `DOCUMENT TO TEMPLATE: ${String(purpose || '').trim()}`,
    audience.trim() ? `AUDIENCE: ${audience.trim()}` : null,
    sections.trim() ? `SECTIONS THE USER WANTS (keep their order and wording where sensible):\n${sections.trim()}` : null,
  ].filter(Boolean).join('\n\n');
}
